import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../pages/AuthContext';
import "../styles/Customer.css";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
    faWarehouse, 
    faUserPlus, 
    faUser, 
    faPhone, 
    faEnvelope, 
    faMapMarkerAlt, 
    faSave, 
    faTimes, 
    faSpinner, 
    faCheckCircle, 
    faExclamationTriangle,
    faStore,
    faExchangeAlt,
    faTag,
    faChevronDown,
    faBoxes,
    faBuilding,
    faUsers,
    faTachometerAlt
} from '@fortawesome/free-solid-svg-icons';

// Create translator instance for Fabrics Godown
const createFabGodownTranslator = () => {
    let cache = new Map();
    let preloadedLanguages = new Set(['en', 'ur', 'ar', 'es']);

    const loadFromCache = () => {
        try {
            const cached = localStorage.getItem('ourzest_translations');
            if (cached) {
                const parsed = JSON.parse(cached);
                Object.entries(parsed).forEach(([key, value]) => {
                    cache.set(key, value);
                });
            }
        } catch (e) {
            console.log("No cache found in Fab Godown Form");
        }
    };

    const saveToCache = () => {
        try {
            const cacheObj = {};
            cache.forEach((value, key) => {
                cacheObj[key] = value;
            });
            localStorage.setItem('ourzest_translations', JSON.stringify(cacheObj));
        } catch (e) {
            console.error("Error saving to cache:", e);
        }
    };

    const translateAPI = async (text, targetLang) => {
        if (targetLang === 'en') return text;

        try {
            const response = await fetch('https://translate.argosopentech.com/translate', {
                method: 'POST',
                body: JSON.stringify({ q: text, source: 'en', target: targetLang }),
                headers: { 'Content-Type': 'application/json' }
            });

            if (response.ok) {
                const data = await response.json();
                return data.translatedText;
            }
        } catch (e) {
            console.log("LibreTranslate failed, trying MyMemory");
        }

        try {
            const response = await fetch(
                `https://api.mymemory.translated.net/get?q=${encodeURIComponent(text)}&langpair=en|${targetLang}`
            );
            if (response.ok) {
                const data = await response.json();
                return data.responseData.translatedText;
            }
        } catch (e) {
            console.log("MyMemory translation failed");
        }

        return text;
    };

    const translateText = async (text, targetLang) => {
        const cacheKey = `${text}_${targetLang}`;

        if (cache.has(cacheKey)) {
            return cache.get(cacheKey);
        }

        const translated = await translateAPI(text, targetLang);
        cache.set(cacheKey, translated);
        saveToCache();

        return translated;
    };

    const defaultTexts = {
        addGodown: 'Add Godown',
        fillDetails: 'Fill in the godown details below',
        godownName: 'Godown Name',
        address: 'Address',
        phoneNumber: 'Phone Number',
        email: 'Email',
        contactPerson: 'Contact Person',
        capacity: 'Capacity',
        status: 'Status',
        selectStatus: 'Select status',
        saveGodown: 'Save Godown',
        clearButton: 'Clear',
        adding: 'Adding...',
        godownAdded: 'Godown saved successfully!',
        failedAdd: 'Failed to save godown',
        networkError: 'Network error occurred',
        nameRequired: 'Godown name is required',
        nameTooShort: 'Name must be at least 2 characters',
        phoneInvalid: 'Please enter a valid phone number',
        emailInvalid: 'Please enter a valid email address',
        success: 'Success',
        error: 'Error',
        warning: 'Warning',
        selectShop: 'Select a shop first',
        currentShop: 'Current Shop',
        willAddToShop: 'Godown will be added to this shop',
        noShopSelected: 'No shop selected. Please switch to a shop first.',
        switchShop: 'Switch Shop',
        shopSwitched: 'Shop switched successfully',
        shopSwitchFailed: 'Failed to switch shop',
        noShopPermission: 'You do not have permission to add godowns to this shop',
        loadingShops: 'Loading shops...',
        noShops: 'No shops available',
        active: 'Active',
        inactive: 'Inactive',
        maintenance: 'Maintenance',
        current: 'Current',
        optional: 'Optional',
        editGodown: 'Edit Godown',
        updating: 'Updating...',
        godownUpdated: 'Godown updated successfully!',
        godownDelete: 'Delete Godown',
        deleteConfirm: 'Are you sure you want to delete this godown?',
        deleteSuccess: 'Godown deleted successfully',
        deleteFailed: 'Failed to delete godown'
    };

    const getTranslations = async (lang) => {
        if (lang === 'en') {
            return defaultTexts;
        }

        const translations = {};
        const promises = [];

        Object.keys(defaultTexts).forEach(key => {
            const cacheKey = `${defaultTexts[key]}_${lang}`;
            if (cache.has(cacheKey)) {
                translations[key] = cache.get(cacheKey);
            } else {
                promises.push(
                    translateText(defaultTexts[key], lang).then(translated => {
                        translations[key] = translated;
                    })
                );
            }
        });

        if (promises.length > 0) {
            await Promise.all(promises);
            saveToCache();
        }

        return translations;
    };

    loadFromCache();

    return {
        getTranslations,
        translateText
    };
};

const fabGodownTranslator = createFabGodownTranslator();

// Toast Notification Component
const ToastNotification = ({ toast, setToast, translations }) => {
    const getIconForType = (type) => {
        switch (type) {
            case 'success':
                return faCheckCircle;
            case 'error':
                return faExclamationTriangle;
            case 'warning':
                return faExclamationTriangle;
            default:
                return faCheckCircle;
        }
    };

    const getTitleForType = (type) => {
        switch (type) {
            case 'success':
                return translations.success || 'Success';
            case 'error':
                return translations.error || 'Error';
            case 'warning':
                return translations.warning || 'Warning';
            default:
                return '';
        }
    };

    if (!toast.show) return null;

    const opacity = Math.max(toast.progress / 100, 0.1);

    return (
        <div 
            className={`toast-notification ${toast.type} ${toast.direction === 'rtl' ? 'toast-rtl' : 'toast-ltr'}`}
            style={{ opacity }}
        >
            <div className="toast-content">
                <div className="toast-icon">
                    <FontAwesomeIcon icon={getIconForType(toast.type)} />
                </div>
                <div className="toast-text">
                    <div className="toast-title">
                        {getTitleForType(toast.type)}
                    </div>
                    <div className="toast-message">
                        {toast.message}
                    </div>
                </div>
                <button 
                    className="toast-close"
                    onClick={() => setToast(prev => ({ ...prev, show: false }))}
                >
                    <FontAwesomeIcon icon={faTimes} />
                </button>
            </div>
            <div className="toast-timer">
                <div 
                    className="toast-progress-bar"
                    style={{ 
                        width: `${toast.progress}%`,
                        transition: 'width 0.1s linear'
                    }}
                ></div>
                <div className="toast-countdown">
                    {toast.timeLeft}s
                </div>
            </div>
        </div>
    );
};

// Shop Selector Modal Component
const ShopSelectorModal = ({ isOpen, onClose, shops, currentShop, onSelectShop, translations, loading }) => {
    if (!isOpen) return null;

    const handleShopSelect = (shop) => {
        onSelectShop(shop);
        onClose();
    };

    return (
        <div className="modal-overlay">
            <div className="modal-content shop-selector-modal">
                <div className="modal-header">
                    <h3>{translations.switchShop || 'Switch Shop'}</h3>
                    <button className="modal-close" onClick={onClose}>
                        <FontAwesomeIcon icon={faTimes} />
                    </button>
                </div>
                <div className="modal-body">
                    {loading ? (
                        <div className="loading-shops">
                            <div className="loading-spinner-small"></div>
                            <p>{translations.loadingShops || 'Loading shops...'}</p>
                        </div>
                    ) : shops.length === 0 ? (
                        <div className="no-shops">
                            <p>{translations.noShops || 'No shops available'}</p>
                        </div>
                    ) : (
                        <div className="shop-list">
                            {shops.map((shop) => (
                                <div 
                                    key={shop.shop_id}
                                    className={`shop-item ${currentShop?.shop_id === shop.shop_id ? 'active' : ''}`}
                                    onClick={() => handleShopSelect(shop)}
                                >
                                    <div className="shop-item-icon">
                                        <FontAwesomeIcon icon={faStore} />
                                    </div>
                                    <div className="shop-item-details">
                                        <div className="shop-item-name">{shop.shop_name}</div>
                                        <div className="shop-item-code">{shop.shop_code || `ID: ${shop.shop_id}`}</div>
                                        {shop.shop_role && (
                                            <div className="shop-item-role">Role: {shop.shop_role}</div>
                                        )}
                                    </div>
                                    {currentShop?.shop_id === shop.shop_id && (
                                        <div className="shop-item-current">
                                            <FontAwesomeIcon icon={faCheckCircle} />
                                            <span>{translations.current || 'Current'}</span>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

const AddFabGodown = () => {
    const { user, currentShop, switchShop, getShopsForSwitch } = useAuth();
    const [formData, setFormData] = useState({
        godown_name: '',
        address: '',
        phone: '',
        email: '',
        contact_person: '',
        capacity: '',
        status: 'active',
        themeColor: localStorage.getItem('ourzest_theme') || '#6366f1'
    });
    
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [currentLanguage, setCurrentLanguage] = useState(() => {
        return localStorage.getItem('ourzest_language') || 'en';
    });
    const [translations, setTranslations] = useState({});
    
    // Toast state
    const [toast, setToast] = useState({
        show: false,
        type: '',
        message: '',
        direction: 'ltr',
        progress: 100,
        timeLeft: 3
    });
    
    // Shop selector state
    const [showShopSelector, setShowShopSelector] = useState(false);
    const [availableShops, setAvailableShops] = useState([]);
    const [loadingShops, setLoadingShops] = useState(false);
    
    // Status dropdown state
    const [showStatusDropdown, setShowStatusDropdown] = useState(false);
    const [statusSearchTerm, setStatusSearchTerm] = useState('Active');
    
    // Refs for duplicate prevention
    const isSubmittingRef = useRef(false);
    const submissionLockRef = useRef(false);
    const lastSubmissionRef = useRef(null);
    const toastTimerRef = useRef(null);
    const formRef = useRef(null);
    const statusDropdownRef = useRef(null);
    const submitButtonRef = useRef(null);

    // Generate unique submission ID
    const generateSubmissionId = () => {
        return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    };

    // Status options
    const statusOptions = [
        { value: 'active', label: 'active', displayName: 'Active' },
        { value: 'inactive', label: 'inactive', displayName: 'Inactive' },
        { value: 'maintenance', label: 'maintenance', displayName: 'Maintenance' }
    ];

    // Filtered statuses based on search
    const filteredStatuses = statusSearchTerm.trim() === '' 
        ? statusOptions 
        : statusOptions.filter(status => 
            status.displayName.toLowerCase().includes(statusSearchTerm.toLowerCase()) ||
            status.value.toLowerCase().includes(statusSearchTerm.toLowerCase())
          );

    // Function to show toast
    const showToast = useCallback((type, message) => {
        if (toastTimerRef.current) {
            clearInterval(toastTimerRef.current);
        }
        
        const isRTL = currentLanguage === 'ur' || currentLanguage === 'ar';
        const direction = isRTL ? 'rtl' : 'ltr';
        
        setToast({
            show: true,
            type,
            message,
            direction,
            progress: 100,
            timeLeft: 30
        });

        let timeLeft = 30;
        const timer = setInterval(() => {
            timeLeft -= 0.1;
            const progress = (timeLeft / 30) * 100;

            setToast(prev => ({
                ...prev,
                progress,
                timeLeft: parseFloat(timeLeft.toFixed(1))
            }));

            if (timeLeft <= 0) {
                clearInterval(timer);
                setToast(prev => ({ ...prev, show: false }));
            }
        }, 100);

        toastTimerRef.current = timer;
    }, [currentLanguage]);

    // Clean up timers on component unmount
    useEffect(() => {
        return () => {
            if (toastTimerRef.current) {
                clearInterval(toastTimerRef.current);
            }
            isSubmittingRef.current = false;
            submissionLockRef.current = false;
        };
    }, []);

    // Handle click outside dropdowns
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (statusDropdownRef.current && !statusDropdownRef.current.contains(event.target)) {
                setShowStatusDropdown(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    // Main submit function with enhanced duplicate prevention
    const submitGodown = useCallback(async (submitData, submissionId) => {
        // Double-check lock before proceeding
        if (isSubmittingRef.current || submissionLockRef.current) {
            console.log('🚫 Submission blocked - already in progress (double-check)');
            return false;
        }

        // Set locks
        isSubmittingRef.current = true;
        submissionLockRef.current = true;
        
        // Disable submit button visually
        if (submitButtonRef.current) {
            submitButtonRef.current.disabled = true;
        }

        try {
            const loggedInUsername = user?.username || 'Unknown';
            
            if (!loggedInUsername || loggedInUsername === 'Unknown') {
                throw new Error("User not authenticated");
            }
            
            const godownData = {
                action: 'add',
                godown_name: submitData.godown_name,
                address: submitData.address,
                phone: submitData.phone,
                email: submitData.email,
                contact_person: submitData.contact_person,
                capacity: submitData.capacity,
                status: submitData.status,
                username: loggedInUsername,
                shop_id: currentShop.shop_id,
                submission_id: submissionId,
                submission_timestamp: Date.now()
            };
            
            console.log('📤 Submitting godown data:', godownData);
            
            const response = await fetch('https://ourzestapp.online/oz_backend/fab_godown.php', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(godownData)
            });

            const data = await response.json();
            console.log('📥 Godown API Response:', data);

            if (data.status === 'success') {
                showToast('success', translations.godownAdded || 'Godown saved successfully!');
                
                // Clear form after successful submission
                setFormData(prev => ({
                    ...prev,
                    godown_name: '',
                    address: '',
                    phone: '',
                    email: '',
                    contact_person: '',
                    capacity: '',
                    status: 'active',
                    themeColor: prev.themeColor
                }));
                setStatusSearchTerm('Active');
                setErrors({});
                
                if (formRef.current) {
                    formRef.current.reset();
                }
                
                return true;
            } else {
                if (data.duplicate_prevention || data.duplicate_database) {
                    showToast('warning', data.message || 'Duplicate submission detected');
                } else {
                    showToast('error', data.message || translations.failedAdd || 'Failed to save godown');
                }
                return false;
            }
        } catch (error) {
            console.error('❌ Error adding godown:', error);
            showToast('error', error.message || translations.networkError || 'Network error occurred');
            return false;
        } finally {
            // Release locks after delay (prevent rapid resubmission)
            setTimeout(() => {
                isSubmittingRef.current = false;
                submissionLockRef.current = false;
                setLoading(false);
                
                // Re-enable submit button
                if (submitButtonRef.current) {
                    submitButtonRef.current.disabled = false;
                }
            }, 2000);
        }
    }, [currentShop, user, translations, showToast]);

    // Debounced submit function with client-side duplicate prevention
    const debouncedSubmit = useCallback(async (submitData) => {
        // Check if already submitting
        if (isSubmittingRef.current || submissionLockRef.current) {
            console.log('🚫 Submission blocked - already in progress');
            showToast('warning', 'Please wait, submission in progress...');
            return;
        }

        const now = Date.now();
        const submissionId = generateSubmissionId();
        
        // Check for duplicate submission within 5 seconds
        if (lastSubmissionRef.current) {
            const timeDiff = now - lastSubmissionRef.current.time;
            const dataMatch = JSON.stringify(submitData) === lastSubmissionRef.current.data;
            
            if (timeDiff < 5000 && dataMatch) {
                console.log('🚫 Duplicate submission prevented (client-side)');
                showToast('warning', 'Please wait a moment before submitting again');
                return;
            }
        }

        // Store this submission
        lastSubmissionRef.current = {
            time: now,
            data: JSON.stringify(submitData),
            id: submissionId
        };

        setLoading(true);
        
        // Call the actual submit function
        await submitGodown(submitData, submissionId);
        
    }, [submitGodown, showToast]);

    // Handle form submit
    const handleSubmit = async (e) => {
        e.preventDefault();
        e.stopPropagation();
        
        console.log('🔔 Form submit triggered at:', new Date().toISOString());

        // Prevent default form submission behavior
        if (e.nativeEvent) {
            e.nativeEvent.preventDefault();
            e.nativeEvent.stopImmediatePropagation();
        }

        // Check if already submitting
        if (isSubmittingRef.current || submissionLockRef.current) {
            console.log('🚫 Submit prevented - already processing');
            showToast('warning', 'Please wait, already processing...');
            return;
        }

        if (!currentShop) {
            showToast('error', translations.selectShop || 'Please select a shop first');
            setShowShopSelector(true);
            return;
        }
        
        if (!validateForm()) {
            return;
        }
        
        // Call debounced submit
        await debouncedSubmit(formData);
    };

    // Validate form
    const validateForm = () => {
        const newErrors = {};
        
        if (!formData.godown_name.trim()) {
            newErrors.godown_name = translations.nameRequired || 'Godown name is required';
        } else if (formData.godown_name.trim().length < 2) {
            newErrors.godown_name = translations.nameTooShort || 'Name must be at least 2 characters';
        }
        
        if (formData.phone && !/^[\d\s\-\+\(\)]{10,15}$/.test(formData.phone)) {
            newErrors.phone = translations.phoneInvalid || 'Please enter a valid phone number';
        }
        
        if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
            newErrors.email = translations.emailInvalid || 'Please enter a valid email address';
        }
        
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    // Handle input changes
    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        
        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: '' }));
        }
    };

    // Handle status selection
    const selectStatus = (status) => {
        setFormData(prev => ({
            ...prev,
            status: status.value
        }));
        setStatusSearchTerm(status.displayName);
        setShowStatusDropdown(false);
    };

    const handleClear = () => {
        // Only allow clear if not submitting
        if (isSubmittingRef.current || submissionLockRef.current) {
            showToast('warning', 'Please wait for current submission to complete');
            return;
        }
        
        setFormData(prev => ({
            ...prev,
            godown_name: '',
            address: '',
            phone: '',
            email: '',
            contact_person: '',
            capacity: '',
            status: 'active',
            themeColor: prev.themeColor
        }));
        setStatusSearchTerm('Active');
        setErrors({});
        lastSubmissionRef.current = null;
    };

    const handleSwitchShop = async (shop) => {
        // Don't allow shop switch during submission
        if (isSubmittingRef.current || submissionLockRef.current) {
            showToast('warning', 'Please wait for current submission to complete');
            return;
        }
        
        try {
            const success = await switchShop(
                shop.shop_id, 
                shop.shop_name, 
                shop.shop_code
            );
            
            if (success) {
                showToast('success', translations.shopSwitched || 'Shop switched successfully');
            } else {
                showToast('error', translations.shopSwitchFailed || 'Failed to switch shop');
            }
        } catch (error) {
            console.error('Error switching shop:', error);
            showToast('error', translations.shopSwitchFailed || 'Failed to switch shop');
        }
    };

    // Apply theme
    useEffect(() => {
        const applyTheme = (color) => {
            try {
                if (!/^#[0-9A-F]{6}$/i.test(color)) color = '#6366f1';
                
                const hex = color.replace('#', '');
                const r = parseInt(hex.substring(0, 2), 16);
                const g = parseInt(hex.substring(2, 4), 16);
                const b = parseInt(hex.substring(4, 6), 16);

                const adjust = (amount) => {
                    let newR = Math.max(0, Math.min(255, r + amount));
                    let newG = Math.max(0, Math.min(255, g + amount));
                    let newB = Math.max(0, Math.min(255, b + amount));
                    return `#${newR.toString(16).padStart(2, '0')}${newG.toString(16).padStart(2, '0')}${newB.toString(16).padStart(2, '0')}`;
                };

                const palette = {
                    'customer-primary-color': color,
                    'customer-primary-dark': adjust(-40),
                    'customer-primary-light': adjust(40),
                    'customer-primary-ultralight': adjust(20),
                    'customer-primary-gradient': `linear-gradient(135deg, ${color}, ${adjust(-20)})`,
                    'customer-primary-shadow': `${color}20`
                };

                Object.entries(palette).forEach(([key, value]) => {
                    document.documentElement.style.setProperty(`--${key}`, value);
                });
            } catch (err) {
                console.error("Error applying theme:", err);
            }
        };

        applyTheme(formData.themeColor);

        const handleThemeChange = () => {
            const theme = localStorage.getItem('ourzest_theme') || '#6366f1';
            if (theme !== formData.themeColor) {
                setFormData(prev => ({ ...prev, themeColor: theme }));
                applyTheme(theme);
            }
        };

        handleThemeChange();
        window.addEventListener('storage', handleThemeChange);
        const interval = setInterval(handleThemeChange, 1000);

        return () => {
            window.removeEventListener('storage', handleThemeChange);
            clearInterval(interval);
        };
    }, [formData.themeColor]);

    // Load translations
    useEffect(() => {
        const loadTranslations = async () => {
            const trans = await fabGodownTranslator.getTranslations(currentLanguage);
            setTranslations(trans);
        };
        loadTranslations();
    }, [currentLanguage]);

    // Listen for language changes
    useEffect(() => {
        const handleLanguageChange = () => {
            const lang = localStorage.getItem('ourzest_language') || 'en';
            if (lang !== currentLanguage) {
                setCurrentLanguage(lang);
            }
        };

        handleLanguageChange();
        window.addEventListener('storage', handleLanguageChange);
        const interval = setInterval(handleLanguageChange, 1000);

        return () => {
            window.removeEventListener('storage', handleLanguageChange);
            clearInterval(interval);
        };
    }, [currentLanguage]);

    // Apply RTL direction
    useEffect(() => {
        const isRTL = currentLanguage === 'ur' || currentLanguage === 'ar';
        
        if (isRTL) {
            document.documentElement.dir = 'rtl';
            document.body.dir = 'rtl';
            document.documentElement.style.direction = 'rtl';
        } else {
            document.documentElement.dir = 'ltr';
            document.body.dir = 'ltr';
            document.documentElement.style.direction = 'ltr';
        }
    }, [currentLanguage]);

    // Load available shops
    useEffect(() => {
        const loadAvailableShops = async () => {
            if (!user) return;
            
            setLoadingShops(true);
            try {
                const shopsData = await getShopsForSwitch();
                setAvailableShops(shopsData.shops || []);
            } catch (error) {
                console.error('Error loading shops:', error);
                setAvailableShops([]);
            } finally {
                setLoadingShops(false);
            }
        };

        loadAvailableShops();
    }, [user, getShopsForSwitch]);

    const isRTL = currentLanguage === 'ur' || currentLanguage === 'ar';

    // Render shop indicator
    const renderShopIndicator = () => {
        if (!currentShop) {
            return (
                <div className="shop-indicator-modern no-shop">
                    <div className="shop-badge">
                        <FontAwesomeIcon icon={faExclamationTriangle} />
                        <span className="shop-warning">
                            {translations.noShopSelected || 'No shop selected'}
                        </span>
                    </div>
                    <button 
                        className="btn-modern btn-switch-shop"
                        onClick={() => setShowShopSelector(true)}
                        disabled={isSubmittingRef.current || submissionLockRef.current}
                    >
                        <FontAwesomeIcon icon={faExchangeAlt} />
                        <span>{translations.switchShop || 'Switch Shop'}</span>
                    </button>
                </div>
            );
        }
        return null;
    };

    return (
        <div className={`customer-form-independent ${isRTL ? 'rtl' : ''}`}>
            {/* Toast Notification */}
            <ToastNotification 
                toast={toast}
                setToast={setToast}
                translations={translations}
            />

            {/* Shop Selector Modal */}
            <ShopSelectorModal
                isOpen={showShopSelector}
                onClose={() => setShowShopSelector(false)}
                shops={availableShops}
                currentShop={currentShop}
                onSelectShop={handleSwitchShop}
                translations={translations}
                loading={loadingShops}
            />

            {/* Modern Form Header */}
            <div className="form-header-modern">
                <div className="header-content">
                    <div className="header-icon">
                        <FontAwesomeIcon icon={faWarehouse} />
                    </div>
                    <div className="header-text">
                        <h1>{translations.addGodown || 'Save Godown'}</h1>
                        <p>{translations.fillDetails || 'Fill in the godown details below'}</p>
                    </div>
                </div>
                
                {/* Shop Indicator */}
                {renderShopIndicator()}
            </div>

            {/* Modern Form */}
            <form 
                ref={formRef}
                onSubmit={handleSubmit} 
                className="modern-form"
                noValidate
            >
                <div className="form-grid-modern">
                    {/* Godown Name Field */}
                    <div className={`form-group-modern ${errors.godown_name ? 'has-error' : ''}`}>
                        <div className="input-container">
                            <FontAwesomeIcon icon={faBuilding} className="input-icon" />
                            <input
                                type="text"
                                id="godown_name"
                                name="godown_name"
                                value={formData.godown_name}
                                onChange={handleChange}
                                required
                                placeholder={translations.godownName || 'Enter godown name'}
                                disabled={loading || isSubmittingRef.current}
                                dir={isRTL ? 'rtl' : 'ltr'}
                            />
                            <label htmlFor="godown_name" className="input-label">
                                {translations.godownName || 'Godown Name'}
                                <span className="required">*</span>
                            </label>
                        </div>
                        {errors.godown_name && (
                            <div className="error-message-modern">
                                <FontAwesomeIcon icon={faExclamationTriangle} />
                                {errors.godown_name}
                            </div>
                        )}
                    </div>

                    

                    

                  
                    {/* Capacity Field */}
                    <div className="form-group-modern">
                        <div className="input-container">
                            <FontAwesomeIcon icon={faBoxes} className="input-icon" />
                            <input
                                type="text"
                                id="capacity"
                                name="capacity"
                                value={formData.capacity}
                                onChange={handleChange}
                                placeholder={translations.capacity || 'Enter capacity (e.g., 1000 sq ft)'}
                                disabled={loading || isSubmittingRef.current}
                                dir={isRTL ? 'rtl' : 'ltr'}
                            />
                            <label htmlFor="capacity" className="input-label">
                                {translations.capacity || 'Capacity'}
                                <span className="optional">{translations.optional || ' Optional'}</span>
                            </label>
                        </div>
                    </div>

                    {/* Status Field */}
                    <div className="form-group-modern" ref={statusDropdownRef}>
                        <div className="input-container">
                            <FontAwesomeIcon icon={faTachometerAlt} className="input-icon" />
                            <input
                                type="text"
                                id="status_search"
                                value={statusSearchTerm}
                                onChange={(e) => {
                                    setStatusSearchTerm(e.target.value);
                                    setShowStatusDropdown(true);
                                    if (formData.status && 
                                        !statusOptions.find(s => 
                                            s.displayName.toLowerCase() === e.target.value.toLowerCase()
                                        )) {
                                        setFormData(prev => ({ ...prev, status: '' }));
                                    }
                                }}
                                onFocus={() => setShowStatusDropdown(true)}
                                placeholder={translations.selectStatus || 'Select status'}
                                disabled={loading || isSubmittingRef.current}
                                dir={isRTL ? 'rtl' : 'ltr'}
                                autoComplete="off"
                            />
                            <label htmlFor="status_search" className="input-label">
                                {translations.status || 'Status'}
                            </label>
                            <FontAwesomeIcon icon={faChevronDown} className="dropdown-icon" />
                        </div>

                        {/* Status Dropdown */}
                        {showStatusDropdown && (
                            <div className="category-dropdown">
                                {filteredStatuses.length > 0 ? (
                                    filteredStatuses.map(status => (
                                        <div
                                            key={status.value}
                                            className={`category-option ${formData.status === status.value ? 'selected' : ''}`}
                                            onClick={() => selectStatus(status)}
                                        >
                                            <div className="category-name">
                                                {translations[status.label] || status.displayName}
                                            </div>
                                            <div className="category-value">
                                                {status.value}
                                            </div>
                                        </div>
                                    ))
                                ) : (
                                    <div className="category-dropdown empty">
                                        <FontAwesomeIcon icon={faExclamationTriangle} />
                                        No statuses found
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>

                {/* Address Field - Full Width */}
                <div className="form-group-modern">
                    <div className="input-container">
                        <FontAwesomeIcon icon={faMapMarkerAlt} className="input-icon" />
                        <textarea
                            id="address"
                            name="address"
                            value={formData.address}
                            onChange={handleChange}
                            rows="3"
                            placeholder={translations.address || 'Enter godown address'}
                            disabled={loading || isSubmittingRef.current}
                            dir={isRTL ? 'rtl' : 'ltr'}
                        ></textarea>
                        <label htmlFor="address" className="input-label">
                            {translations.address || 'Address'}
                            <span className="optional">{translations.optional || ' Optional'}</span>
                        </label>
                    </div>
                </div>

                {/* Form Actions */}
                <div className="form-actions-modern">
                    <button
                        type="button"
                        className="btn-modern btn-secondary-modern"
                        onClick={handleClear}
                        disabled={loading || isSubmittingRef.current || submissionLockRef.current}
                    >
                        <FontAwesomeIcon icon={faTimes} />
                        <span>{translations.clearButton || 'Clear'}</span>
                    </button>
                    <button
                        ref={submitButtonRef}
                        type="submit"
                        className="btn-modern btn-primary-modern"
                        disabled={loading || !currentShop || isSubmittingRef.current || submissionLockRef.current}
                    >
                        {loading || isSubmittingRef.current ? (
                            <>
                                <div className="btn-spinner"></div>
                                <span>{translations.adding || 'Adding...'}</span>
                            </>
                        ) : (
                            <>
                                <span>{translations.saveGodown || 'Save Godown'}</span>
                                <FontAwesomeIcon icon={faSave} />
                            </>
                        )}
                    </button>
                </div>
                
                {/* Submission Status Indicator */}
                {(isSubmittingRef.current || submissionLockRef.current) && (
                    <div className="submission-status">
                        <div className="submission-status-text">
                            <FontAwesomeIcon icon={faSpinner} spin />
                            <span>Processing your submission...</span>
                        </div>
                    </div>
                )}
            </form>

            {/* Loading Overlay */}
            {loading && (
                <div className="loading-overlay">
                    <div className="loading-content">
                        <div className="loading-spinner"></div>
                        <p>{translations.adding || 'Adding...'}</p>
                        <p className="loading-subtext">Please wait while we save godown data</p>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AddFabGodown;